import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchOrganizerDirectory } from '@/api/venueApi';

interface Props {
    value: string[];
    onChange: (next: string[]) => void;
}

export default function OrganizerAccessPicker({ value, onChange }: Props) {
    const [search, setSearch] = useState('');
    const { data = [], isLoading, isError, refetch } = useQuery({ queryKey: ['organizer-directory'], queryFn: fetchOrganizerDirectory, staleTime: 5 * 60 * 1000 });
    const selected = useMemo(() => new Set(value), [value]);
    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return data.filter(o => !q || `${o.orgName} ${o.shortName ?? ''}`.toLowerCase().includes(q)).slice(0, 50);
    }, [data, search]);
    const selectedOptions = useMemo(() => data.filter(o => selected.has(o._id)), [data, selected]);
    const toggle = (id: string) => onChange(selected.has(id) ? value.filter(v => v !== id) : [...value, id]);

    return (
        <div className="mt-3 flex flex-col gap-2" aria-label="Organizers with access">
            <input
                type="search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search organizers..."
                aria-label="Search organizers"
                className="h-10 px-4 rounded-2xl bg-grey99 dark:bg-white/[0.06] text-sm outline-none"
            />
            {isLoading && <p className="text-xs text-grey40 dark:text-nightMuted">Loading organizers...</p>}
            {isError && (
                <p role="alert" className="text-xs text-red-500">
                    Could not load organizers. <button type="button" className="underline" onClick={() => refetch()}>Retry</button>
                </p>
            )}
            {!isLoading && !isError && data.length === 0 && (
                <p className="text-xs text-grey40 dark:text-nightMuted">No organizers available.</p>
            )}
            {!isLoading && !isError && data.length > 0 && visible.length === 0 && (
                <p className="text-xs text-grey40 dark:text-nightMuted">No organizers found.</p>
            )}
            {selectedOptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5" aria-label="Selected organizers">
                    {selectedOptions.map(o => (
                        <button
                            key={o._id}
                            type="button"
                            onClick={() => toggle(o._id)}
                            aria-label={`Remove ${o.orgName}`}
                            className="rounded-full bg-primary99 dark:bg-nightAccent/10 px-3 py-1 text-xs text-grey10 dark:text-white"
                        >
                            {o.orgName} ×
                        </button>
                    ))}
                </div>
            )}
            <ul className="max-h-48 overflow-y-auto flex flex-col gap-1">
                {visible.map(o => (
                    <li key={o._id}>
                        <label className="flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-grey99 dark:hover:bg-white/[0.06] cursor-pointer">
                            <input type="checkbox" checked={selected.has(o._id)} onChange={() => toggle(o._id)} />
                            <span className="min-w-0 truncate text-sm text-grey10 dark:text-white" title={o.orgName}>{o.orgName}</span>
                        </label>
                    </li>
                ))}
            </ul>
            <p className="text-xs text-grey40 dark:text-nightMuted">
                {value.length === 0
                    ? 'No other organizer will have access to this venue.'
                    : `${value.length} organizer${value.length === 1 ? '' : 's'} can find and use this venue. Only your organization's owner, admins and event managers can edit it.`}
            </p>
        </div>
    );
}
